
import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FilterOperation } from '@/hooks/useConnectionData';
import ColumnSelector from './ColumnSelector';
import FilterOperationSelect from './FilterOperationSelect';
import DateRangeFilterInput from './DateRangeFilterInput';

interface NewFilterFormProps {
  columns: string[];
  dateColumns?: string[];
  onAddFilter: (column: string, value: string, operation: FilterOperation) => void;
  tableData: any[];
}

const NewFilterForm: React.FC<NewFilterFormProps> = ({
  columns,
  dateColumns = [],
  onAddFilter,
  tableData
}) => {
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [filterValue, setFilterValue] = useState("");
  const [operation, setOperation] = useState<FilterOperation>('equals');
  const [columnType, setColumnType] = useState<string>('string');

  const isDateColumn =
    !!selectedColumn && dateColumns.includes(selectedColumn);

  useEffect(() => {
    setFilterValue('');
  }, [selectedColumn]);

  // Detect column type when selected column changes
  useEffect(() => {
    if (isDateColumn) {
      setColumnType('date');
      if (operation !== 'between') {
        setOperation('between');
      }
      return;
    }

    if (selectedColumn && tableData && tableData.length > 0) {
      // Find first non-null value to determine type
      let sampleValue = undefined;
      let rowIndex = 0;
      
      while (sampleValue === undefined && rowIndex < tableData.length) {
        const row = tableData[rowIndex];
        if (row && row[selectedColumn] !== undefined && row[selectedColumn] !== null) {
          sampleValue = row[selectedColumn];
          break;
        }
        rowIndex++;
      }
      
      if (sampleValue !== undefined) {
        // Check if it's a number
        const rawValue = String(sampleValue);
        const isNumber = !isNaN(parseFloat(rawValue)) && isFinite(Number(rawValue));
        const newColumnType = isNumber ? 'number' : 'string';
        
        setColumnType(newColumnType);
        
        // Set appropriate default operation based on column type
        if (newColumnType === 'number' && operation === 'contains') {
          setOperation('equals');
        } else if (newColumnType === 'string' && ['gt', 'lt', 'gte', 'lte'].includes(operation)) {
          setOperation('equals');
        }
      } else {
        setColumnType('string');
      }
    }
  }, [selectedColumn, tableData, operation, isDateColumn, dateColumns]);

  const handleApplyFilter = () => {
    if (selectedColumn) {
      // For operations that don't need a value, we can apply without requiring filterValue
      const needsValue = !['is_blank', 'is_not_blank'].includes(operation);
      
      if ((needsValue && filterValue.trim()) || !needsValue) {
        onAddFilter(selectedColumn, filterValue, operation);
        setFilterValue("");
        setSelectedColumn(null);
        setOperation('equals');
      }
    }
  };

  const needsValueInput = !['is_blank', 'is_not_blank'].includes(operation);
  const hasDateRangeValue = isDateColumn && operation === 'between' && filterValue.includes('|');

  return (
    <div className="pt-2 border-t">
      <div className="text-sm mb-2">Add filter:</div>
      <div className="flex flex-col gap-2">
        <ColumnSelector 
          columns={columns}
          selectedColumn={selectedColumn}
          onColumnSelect={setSelectedColumn}
        />

        {!isDateColumn && (
          <FilterOperationSelect
            value={operation}
            onChange={setOperation}
            disabled={!selectedColumn}
            columnType={columnType}
          />
        )}

        {needsValueInput && isDateColumn && (
          <DateRangeFilterInput
            value={filterValue}
            onChange={setFilterValue}
            disabled={!selectedColumn}
          />
        )}

        {needsValueInput && !isDateColumn && (
          <Input
            placeholder="Filter value..."
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            disabled={!selectedColumn}
            type={columnType === 'number' ? 'number' : 'text'}
          />
        )}
        
        <div className="flex justify-end pt-2">
          <Button 
            size="sm" 
            onClick={handleApplyFilter}
            disabled={
              !selectedColumn ||
              (isDateColumn ? !hasDateRangeValue : needsValueInput && !filterValue.trim())
            }
          >
            <Plus size={16} className="mr-1" />
            Add Filter
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewFilterForm;
